<template>
  <!-- <div class="disclaimer">
  <strong>Disclaimer:</strong> The generated plans are not fully accurate as this system is still a work in progress.
  Updated and finalized plans will be available by <strong>February</strong>.
</div> -->

  <div class="transfer">
    <StepIndicator :current="step" :labels="stepLabels" />
    <div class="sr-only" aria-live="polite" ref="liveRegion"></div>
<SchoolPath
  :start="startingSchool?.name || 'Starting School'"
  :dest="destinationSchool?.name || 'Destination School'"
  :startSet="!!startingSchool"
  :destSet="!!destinationSchool"
>
  <template #dest-extra>
    <div v-if="destinationProgram" class="dest-program">
      {{ destinationProgram.display_name || destinationProgram.name }}
    </div>
  </template>

    <template #start-action>
      <button
          v-if="step === 5 && startingSchool"
          class="schoolpath-link btn-inline-cta pulse-once"
          type="button"
          :disabled="loading"
          :aria-disabled="loading ? 'true' : 'false'"
          title="Edit selected courses (Step 2)"
          :aria-label="`Edit ${startingSchool?.name} courses (go to Step 2)`"
          @click="announceAndGoToCourses"
        >
          <i class="fa-regular fa-pen-to-square" aria-hidden="true"></i>
          <span>Edit Courses</span>
        </button>
      </template>
    </SchoolPath>

    <section class="container form">
      <template v-if="loading && step <= 3">
        <div class="text-center" v-if="loading">
          <h4>{{ loadingMsg }}</h4>
          <h3 class="loader"></h3>
        </div>
      </template>
      <LoadingState v-else-if="loading" :message="loadingMsg" :progress="loadingProgress" :steps="loadingSteps"
        :currentStep="currentLoadingStep" :showMinorTip="hasMinor && !!selectedMinor" />

      <ErrorState v-else-if="error" :message="errorMsg" :details="errorDetails" @reset="resetError" />

      <template v-else>
        <!-- Step 1: Starting School -->
        <div v-if="step === 1">
          <h3>1. Select your community college:</h3>
          <SearchSelect class="school-selection-search" :items="startingSchools" label-key="name"
            :placeholder="startingSchool?.name || 'Search Schools'" @item-selected="selectStartingSchool" />
          <div class="actions">
            <button v-if="startingSchool" class="ube-btn" @click="step++">Next</button>
          </div>
        </div>

        <!-- Step 2: Completed Courses -->
        <div v-else-if="step === 2">
          <h3>2. Select courses at {{ startingSchool?.name }}:</h3>
          <SearchSelect class="course-selection" :items="startingSchoolCourses" label-key="full_name"
            placeholder="Select Course" @item-selected="addCourse" />
          <CourseList :courses="takenCourses" @remove="removeCourse" />
          <div class="actions">
            <button class="ube-btn" @click="step--">Back</button>
            <button class="ube-btn" @click="lookupCoursesAndProceed">Next</button>
          </div>
        </div>

        <!-- Step 3: Destination Program -->
        <div v-else-if="step === 3">
          <h3>3. Select destination program at {{ destinationSchool?.name || config.institutions.destination.name }}:</h3>

          <SearchSelect class="program-selection-search" :items="programs" label-key="display_name"
            :placeholder="destinationProgram ? (destinationProgram.display_name || destinationProgram.name) : 'Search Degree Programs'"
            @item-selected="selectDegreeProgram" />

          <div v-if="destinationProgram" class="mt-2" style="text-align:center;font-weight:700">
            Selected Program: {{ destinationProgram.display_name || destinationProgram.name }}
          </div>
          <div v-if="minorSelectionEnabled && destinationProgram && hasMinor" class="text-center mt-2">
            <em>{{ minorNotice }}</em>
          </div>
          <div class="actions">
            <button class="ube-btn" @click="step--">Back</button>
            <!-- Show Generate Report button if:
                 1. Minor selection is disabled (feature flag), OR
                 2. Minor selection is enabled but program doesn't require minor -->
            <button
              v-if="requirementsChecked && (!minorSelectionEnabled || !hasMinor)"
              class="ube-btn generate-report"
              @click="onGenerateReport"
            >
              Generate Transfer Report
            </button>

            <!-- Show Next button only if minor selection is enabled AND program requires minor -->
            <button
              v-else-if="requirementsChecked && minorSelectionEnabled && hasMinor"
              class="ube-btn"
              @click="step = 4"
            >
              Next
            </button>
          </div>
        </div>

        <!-- Step 4: Minor (optional) - only shown if feature flag enabled -->
        <div v-else-if="step === 4 && minorSelectionEnabled">
          <h3>4. Select a Minor</h3>
          <p v-if="!minorOptions.length" class="mt-2">No minors available. You can proceed without selecting.</p>

          <SearchSelect v-if="minorOptions.length" class="program-selection-search" :items="minorOptions"
            label-key="name" placeholder="Search Minors" @item-selected="selectMinor" />

          <div v-if="selectedMinor" class="mt-2" style="text-align:center;font-weight:600">
            Selected Minor: {{ selectedMinor.name }}
          </div>

          <div class="actions">
            <button class="ube-btn" @click="goBackToStep3">Back</button>
            <button class="ube-btn generate-report" :disabled="minorOptions.length > 0 && !selectedMinor"
              @click="onGenerateReport">Generate Transfer Report</button>
          </div>
        </div>

        <!-- Step 5: Report -->
        <div v-else-if="step === 5" class="container-fluid transfer two-to-four-year-transfer-container">
          <div class="row two-to-four-year-report-container">

            <!-- Toolbar: both buttons in one line -->
              <div class="col-md-12">
                <div class="report-toolbar">
                  <div class="left">
                    <button
                      class="btn btn-primary"
                      data-toggle="modal"
                      data-target="#reportSummarModal"
                      type="button"
                    >
                      Analyze Transfer Plan
                    </button>
                  </div>
                </div>
              </div>
            <div class="col-md-12 dp-visualization-container p-4">
              <DegreePlanVisualization v-if="transferPlanTerms.length"
                :planTermData="transferPlanTerms"
                :isTransferPlan="true"
                :hadTransfer="true"
                :readOnly="true" />
            </div>

            <ReportSummaryModal :reportData="transferPlanAnalysis" />
            <div class="actions">
              <!-- Back goes to step 4 (minor) if enabled, otherwise step 3 (program) -->
              <button class="ube-btn mt-3" @click="step = minorSelectionEnabled ? 4 : 3">Back</button>
            </div>
          </div>
        </div>

      </template>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, nextTick} from 'vue';
import { onBeforeUnmount } from 'vue';
import SearchSelect from '@/components/SearchSelect.vue';
import StepIndicator from '@/components/StepIndicator.vue';
import SchoolPath from '@/components/SchoolPath.vue';
import CourseList from '@/components/CourseList.vue';
import LoadingState from '@/components/LoadingState.vue';
import ErrorState from '@/components/ErrorState.vue';
import ReportSummaryModal from '@/components/TransferSummaryModal.vue';
// @ts-ignore
import { DegreePlanVisualization } from '@unm-idi/vue-degree-plan/vue-degree-plan.js';
import '@unm-idi/vue-degree-plan/vue-degree-plan.css';

// models
import type School from '@/models/school';
import type Program from '@/models/program';
import type Course from '@/models/course';

// your composables & utils
import { usePrograms } from '@/composables/usePrograms';
import { useLoadingError } from '@/composables/useLoadingError';
import { createTermObjects, detectMinorRequirement, convertToMinorCode, mergeMinorIntoDegreeRequirements } from '@/utils/degreePlanUtils';
import { useMinors } from '@/composables/useMinors';

// tenant config
import { useTenantConfig } from '@/composables/useTenantConfig';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { useLarxPrograms } from '@/composables/useLarxPrograms';

// services
import { getSchools, fetchSchoolCourses, generateCompletionPlan, degreeReqsApi } from '@/services';

// ---------- tenant config & feature flags ----------
const { config, isLarxTenant } = useTenantConfig();
const { minorSelectionEnabled } = useFeatureFlags();
const { programs: larxPrograms, fetchPrograms: fetchLarxPrograms, getRequirements: getLarxRequirements } = useLarxPrograms();

// ---------- state ----------
const step = ref(1);

// Step labels - conditionally include minor step based on feature flag
const stepLabels = ref<string[]>(
  minorSelectionEnabled.value
    ? ['Starting School', 'Completed Courses', 'Destination Program', 'Minor (if required)', 'Transfer Report']
    : ['Starting School', 'Completed Courses', 'Destination Program', 'Transfer Report']
);

// Get schools from config-driven service layer
const { startingSchools: configStartingSchools, destinationSchools: configDestinationSchools } = getSchools();
const startingSchools = ref<School[]>(configStartingSchools);
const destinationSchool = ref<School>(configDestinationSchools[0]);

const startingSchool = ref<School | null>(null);
const startingSchoolCourses = ref<Course[]>([]);

const programs = ref<Program[]>([]);
const destinationProgram = ref<Program | null>(null);

const takenCourses = ref<Course[]>([]);
const courseEquivalencies = ref<Record<string, any>>({});  // Maps course code to { apiCourse, equivalencies }
const selectedMinor = ref<any | null>(null);
const minorOptions = ref<any[]>([]);
const hasMinor = ref(false);
const minorNotice = ref('');

const transferPlanTerms = ref<any[]>([]);
const transferPlanAnalysis = ref<any>({});
const requirementsChecked = ref(false);

const { getActiveMinorsFromCsv, isLoading: minorsLoadingRef, errorMsg: minorsErrorMsgRef } = useMinors();
const liveRegion = ref<HTMLElement | null>(null);
let keyHandler: ((e: KeyboardEvent) => void) | null = null;
  function announce(msg: string) {
    if (!liveRegion.value) return;
    liveRegion.value.textContent = '';
    requestAnimationFrame(() => (liveRegion.value!.textContent = msg));
  }

  async function announceAndGoToCourses() {
    announce('Navigating to edit completed courses in Step 2.');
    await goToCompletedCourses();
    nextTick(() => {
      const el = document.querySelector('.course-selection') || document.querySelector('.form');
      el?.classList.add('flash-highlight');
      setTimeout(() => el?.classList.remove('flash-highlight'), 1200);
    });
  }

  onMounted(() => {
    keyHandler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      if (step.value === 5 && e.key.toLowerCase() === 'e' && tag !== 'input' && tag !== 'textarea') {
        e.preventDefault();
        announceAndGoToCourses();
      }
    };
    window.addEventListener('keydown', keyHandler!);
  });

  onBeforeUnmount(() => {
    if (keyHandler) window.removeEventListener('keydown', keyHandler);
  });

// composables
const { getProgramsFromLocalNdjson, getProgramRequirement } = usePrograms();
const {
  loading, loadingMsg, loadingProgress, currentLoadingStep, loadingSteps,
  error, errorMsg, errorDetails,
  startLoadingWithProgress, updateLoadingProgress, completeLoading,
  handleError, resetError,
} = useLoadingError();

// ---------- lifecycle ----------
onMounted(async () => {
  // Load programs based on tenant data source
  if (isLarxTenant.value) {
    // LARX tenant: fetch programs from LARX API
    await fetchLarxPrograms();
    // Map LARX API programs to local Program type
    programs.value = larxPrograms.value.map(p => ({
      id: p.id,
      name: p.name,
      display_name: p.name,
      code: p.external_id || '',
      disable: false,
      requirements: null,
    })) as unknown as Program[];
  } else {
    // Arizona/S3 tenant: preload via local NDJSON
    programs.value = await getProgramsFromLocalNdjson(destinationSchool.value);  }
    });

// ---------- actions ----------
async function selectStartingSchool(school: School) {
  loading.value = true;
  try {
    takenCourses.value = [];
    startingSchool.value = school;
    startingSchoolCourses.value = await fetchSchoolCourses(school);
    step.value = 2;
  } catch (err: any) {
    console.error('selectStartingSchool failed:', err);
    handleError(err);
  } finally {
    loading.value = false;
  }
}

function addCourse(course: Course) {
  try {
    takenCourses.value.push(course);
  } catch (err: any) {
    console.error('addCourse failed:', err);
    handleError(err);
  }
}

function removeCourse(i: number) {
  try {
    takenCourses.value.splice(i, 1);
  } catch (err: any) {
    console.error('removeCourse failed:', err);
    handleError(err);
  }
}

/**
 * Lookup course IDs and equivalencies when moving from step 2 to 3 (LARX tenants)
 */
async function lookupCoursesAndProceed() {
  if (!isLarxTenant.value) {
    // Non-LARX tenants just proceed
    step.value = 3;
    return;
  }

  loading.value = true;
  loadingMsg.value = 'Looking up course equivalencies...';
  resetError();

  try {
    // Lookup university IDs by name
    const sourceUniversityId = await degreeReqsApi.findUniversityByName(startingSchool.value!.name);
    const destUniversityId = await degreeReqsApi.findUniversityByName(config.institutions.destination.name);

    if (!sourceUniversityId || !destUniversityId) {
      throw new Error('Could not find university IDs');
    }

    // Clear previous equivalencies
    courseEquivalencies.value = {};

    // For each selected course, lookup its ID and equivalencies
    for (const course of takenCourses.value) {
      // Use prefix/num directly from course object (set by fetchSchoolCoursesFromJson)
      const prefix = (course as any).prefix;
      const num = (course as any).num;
      const courseCode = (course as any).code || `${prefix} ${num}`;

      if (prefix && num) {
        // Lookup the course by prefix and num
        const apiCourse = await degreeReqsApi.getCourseByPrefixAndNum(
          sourceUniversityId,
          prefix,
          num
        );

        if (apiCourse) {
          // Get equivalencies for this course at destination
          const equivalencies = await degreeReqsApi.getCourseEquivalencies(
            apiCourse.id,
            destUniversityId
          );

          // Store the equivalencies mapped by course code
          courseEquivalencies.value[courseCode] = {
            apiCourse,
            equivalencies
          };
        } else {
          console.warn(`Could not find course ${courseCode} in LARX API`);
        }
      }
    }

    // Proceed to next step
    step.value = 3;
  } catch (err: any) {
    console.error('lookupCoursesAndProceed failed:', err);
    handleError(err);
  } finally {
    loading.value = false;
  }
}

async function selectDegreeProgram(program: Program) {
  destinationProgram.value = program;
  hasMinor.value = false;
  minorNotice.value = '';
  selectedMinor.value = null;
  minorOptions.value = [];

  if (!minorSelectionEnabled.value) {
    // LARX tenants: no minor detection needed
    requirementsChecked.value = true;
    return;
  }

  // Arizona: async minor detection
  loading.value = true;
  loadingMsg.value = 'Checking program requirements...';
  try {
    const reqs = await getProgramRequirement(program);
    const minorDetected = reqs ? detectMinorRequirement(reqs) : false;
    hasMinor.value = minorDetected;

    if (minorDetected) {
      minorNotice.value = 'This program requires a minor. Please select one in the next step.';
      const minors = await getActiveMinorsFromCsv();
      if (minorsErrorMsgRef.value) {
        throw new Error(`Failed to load minors: ${minorsErrorMsgRef.value}`)
      }
      minorOptions.value = minors;
    }

    requirementsChecked.value = true;
  } catch (err: any) {
    console.error('selectDegreeProgram failed:', err);
    handleError(err);
    requirementsChecked.value = true
  } finally {
    loading.value = false;
  }
}

function selectMinor(minor: any) {
  try {
    selectedMinor.value = minor;
  } catch (err: any) {
    console.error('selectMinor failed:', err);
    handleError(err);
  }
}

function goBackToStep3() {
  try {
    resetError();
    step.value = 3;
    selectedMinor.value = null;
    loadingProgress.value = 0;
    requirementsChecked.value = !!destinationProgram.value?.requirements;
  } catch (err: any) {
    console.error('goBackToStep3 failed:', err);
    handleError(err);
  }
}

function normalizeCourseCode(raw: any) {
  const s = String(raw ?? '').toUpperCase().trim();

  // Turn "SOC101", "SOC-101", "SOC 101" into "SOC 101"
  const m = s.match(/^([A-Z]+)\s*[- ]?\s*(\d.*)$/);
  if (m) return `${m[1]} ${m[2]}`.replace(/\s+/g, ' ').trim();

  return s.replace(/\s+/g, ' ').trim();
}

function slimTakenCourse(c: any) {
  const code = normalizeCourseCode(c?.code);
  const min = Number(c?.minimum_credits ?? c?.credits ?? 0);
  const max = Number(c?.maximum_credits ?? c?.credits ?? min);

  return {
    id: Number(c?.id ?? 0),
    code,
    full_number: code,
    name: c?.name ?? c?.full_name ?? code,
    full_name: c?.full_name ?? c?.name ?? code,
    minimum_credits: min,
    maximum_credits: max,
  } as Course;
}


// ---------- report generation ----------
async function onGenerateReport() {
  step.value = 5;
  startLoadingWithProgress();

  try {
    let requestData: any;

    if (isLarxTenant.value) {
      // --- LARX path ---
      updateLoadingProgress(1, 'Fetching program requirements...');
      const completeReqs = await getLarxRequirements(destinationProgram.value!.id);

      if (!completeReqs) {
        console.warn('No requirements found for program, continuing anyway');
      }

      if (destinationProgram.value) {
        destinationProgram.value = { ...destinationProgram.value, requirements: completeReqs } as Program;
      }

      updateLoadingProgress(3, 'Preparing transfer plan request...');

      const completedCourses = Object.values(courseEquivalencies.value).map((item: any) => ({
        source_course: item.apiCourse,
        equivalencies: item.equivalencies
      }));

      requestData = {
        source_school: startingSchool.value as School,
        dest_program: { ...(destinationProgram.value as any), requirements: completeReqs },
        taken_courses: takenCourses.value as Course[],
        completed_courses: completedCourses,
        requirements: completeReqs,
      };
    } else {
      // --- Arizona path ---
      let minor_program_code: string | null = null;
      let minor_college: string | null = null;
      let minor_name: string | null = null;

      if (hasMinor.value && selectedMinor.value) {
        updateLoadingProgress(1, 'Preparing minor info...');

        const code = selectedMinor.value.code;
        const name = selectedMinor.value.name;

        const minorProgram =
          minorOptions.value.find(p => (p as any).code === code) ||
          minorOptions.value.find(p => (p as any).name === name) ||
          minorOptions.value.find(p =>
            String((p as any).display_name ?? '').toLowerCase()
              .includes(String(name ?? '').toLowerCase())
          );

        if (!minorProgram) throw new Error('Minor program not found in programs list');

        minor_program_code = (minorProgram as any).code ?? null;
        minor_college = (minorProgram as any).college ?? null;
        minor_name = selectedMinor.value.name ?? null;
      }

      updateLoadingProgress(3, 'Preparing transfer plan request...');
      const normalizedTaken = (takenCourses.value || []).map(slimTakenCourse);

      requestData = {
        source_school: startingSchool.value as School,
        dest_program: {
          code: (destinationProgram.value as any)?.code,
          college: (destinationProgram.value as any)?.college,
          name: (destinationProgram.value as any)?.name,
          degree_abbr: (destinationProgram.value as any)?.degree_abbr,
          display_name: (destinationProgram.value as any)?.display_name,
        },
        minor: minor_program_code ? {
          program_code: minor_program_code,
          college: minor_college,
          name: minor_name,
          original_code: selectedMinor.value.code,
        } : null,
        taken_courses: normalizedTaken,
      };
    }

    updateLoadingProgress(4, 'Generating transfer plan...');
    const responseData = await generateCompletionPlan(requestData);

    updateLoadingProgress(5, 'Processing results...');
    applyPlanResponse(responseData);

    completeLoading();
  } catch (err: any) {
    console.error('onGenerateReport failed:', err);
    handleError(err);
  } finally {
    loading.value = false;
  }
}

function applyPlanResponse(data: any) {
  try {
    if (
  destinationProgram.value &&
  data?.dest_requirements &&
  Object.keys(data.dest_requirements).length
) {
  destinationProgram.value.requirements = data.dest_requirements;
}

    transferPlanAnalysis.value = data?.transfer_plan_report || {};

    transferPlanTerms.value = createTermObjects(
      data?.destination_degreeplan || [],
      0,
      {
        includeTransferCourses: true,
        takenCourses: data?.taken_courses || [],
        originalSchool: startingSchool.value?.name || 'Transfer Credit',
      }
    );
  } catch (e) {
    handleError(e);
  }
}
function goToCompletedCourses() {
  if (!startingSchool.value) {
    step.value = 1;
    return;
  }
  step.value = 2;
  nextTick(() => {
    document.querySelector('.form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}
</script>

<style scoped>
.report-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin: 12px 0 16px;
}
.report-toolbar .btn {
  font-weight: 600;
  padding: 6px 14px;
  font-size: 0.9rem; 
  min-width: 160px; 
}
.report-toolbar .right .btn {
  font-size: 0.8rem;    
  padding: 4px 10px;  
  min-width: 120px;    
}


@media (max-width: 576px) {
  .report-toolbar {
    flex-direction: column;
    gap: 8px;
  }
  .report-toolbar .left,
  .report-toolbar .right {
    width: 100%;
    display: flex;
    justify-content: center;
  }
}
.schoolpath-link {
  background:  none;
  border: none;
  padding: 0;
  margin-left: 8px;
  font-weight: 600;
  color:     #403635;             
  text-decoration: underline;
  text-decoration-thickness: 2px;
  text-underline-offset: 3px;
  cursor: pointer;
}

.schoolpath-link:hover {
  text-decoration-color: #0a2e52;  
}

.schoolpath-link:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  text-decoration: none;
}

/* keyboard focus */
.schoolpath-link:focus-visible {
  outline: 3px solid rgba(13, 59, 102, 0.35);
  outline-offset: 2px;
}

/* upgrade the inline link into a button-like pill while staying inline */
.btn-inline-cta {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;           
  border-radius: 9999px;
  border: 1px solid rgba(13,59,102,0.15);
  background: #c3c9d1;
  font-size: 0.75rem;              
  font-weight: 600;
  color: #0a2e52;
  box-shadow: none;
  transition: all 0.15s ease;
}
.disclaimer{
  background:#fff3cd; 
  border:1px solid #ffeeba; 
  color:#856404; padding:12px 16px; 
  border-radius:8px; 
  font-family:Arial, Helvetica, sans-serif;
  text-align: center;
}

.btn-inline-cta i {
  font-size: 0.8rem;
}

.btn-inline-cta:hover {
  background: #f2f6fa;
  transform: none;
  box-shadow: 0 1px 2px rgba(13,59,102,0.15);
}


.btn-inline-cta:active {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(13,59,102,0.18) inset;
}

.btn-inline-cta:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.disclaimer{
  background:#fff3cd; 
  border:1px solid #ffeeba; color:#856404; 
  padding:12px 16px; 
  border-radius:8px; 
  font-family:Arial, Helvetica, sans-serif;
  text-align: center;
}

/* keyboard hint pill */
.btn-inline-cta .kbd {
  font-size: 0.72rem;
  line-height: 1.2;
  padding: 0 6px;
  border-radius: 6px;
  border: 1px solid rgba(0,0,0,0.18);
  background: rgba(0,0,0,0.05);
  font-weight: 600;
}
.btn-inline-cta:focus-visible {
  outline: 3px solid rgba(13,59,102,0.45);
  outline-offset: 3px;
}
.pulse-once {
  animation: pulseOnce 1.1s ease-out 0.35s 1 both;
}
@keyframes pulseOnce {
  0%   { transform: scale(1); box-shadow: 0 0 0 rgba(13,59,102,0); }
  60%  { transform: scale(1.04); box-shadow: 0 0 0 10px rgba(13,59,102,0.10); }
  100% { transform: scale(1); box-shadow: 0 0 0 rgba(13,59,102,0); }
}
.flash-highlight {
  animation: flash 1.1s ease-in-out 1;
}
@keyframes flash {
  0%   { box-shadow: 0 0 0 0 rgba(13,59,102,0); background-color: transparent; }
  25%  { box-shadow: 0 0 0 4px rgba(13,59,102,0.18); background-color: rgba(13,59,102,0.06); }
  100% { box-shadow: 0 0 0 0 rgba(13,59,102,0); background-color: transparent; }
}

@media (prefers-reduced-motion: reduce) {
  .pulse-once, .flash-highlight { animation: none !important; }
  .btn-inline-cta { transition: none; }
}
.sr-only {
  position: absolute !important;
  width: 1px; height: 1px;
  padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0, 0, 0, 0);
  white-space: nowrap; border: 0;
}

</style>

